// Paywall: 90s free conversation, then a one-click $3 weekly pass.
// Stripe Checkout (Apple Pay appears automatically on supported devices).

import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";

import { getDeviceId } from "@/lib/device";
import { isSignedIn, setAuth } from "@/lib/auth";
import PhoneLinkCard from "@/components/PhoneLinkCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaywallSheet({ open, priceUsd = 3, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signedIn, setSignedIn] = useState(() => isSignedIn());

  if (!open) return null;

  const subscribe = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          origin_url: window.location.origin,
          device_id: getDeviceId(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error("checkout_failed");
      window.location.href = data.url; // one click -> Stripe Checkout
    } catch {
      setError("Could not open checkout. Try again.");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="paywall-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Continue with a weekly pass"
      className="fixed inset-0 z-[85] flex items-end justify-center sm:items-center"
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: "hsla(40, 30%, 20%, 0.4)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      />
      <div className="relative z-10 w-full max-w-md cg-card cg-fade-up rounded-t-[2rem] p-7 sm:rounded-[2rem]">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-4 w-4" style={{ color: "hsl(42 60% 50%)" }} strokeWidth={1.8} />
          <p className="text-[11px] uppercase tracking-[0.22em]">Your free minutes are used</p>
        </div>
        <h2
          className="font-serif-display mt-2 text-foreground"
          style={{ fontSize: "1.7rem", lineHeight: 1.25 }}
        >
          Stay on the line.
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Unlimited conversation, every day. Renews automatically at $
          {priceUsd}/week — cancel anytime in one tap from your history page.
        </p>

        {signedIn ? (
          <>
            <button
              type="button"
              data-testid="paywall-subscribe-btn"
              onClick={subscribe}
              disabled={busy}
              className="cg-btn-primary mt-5 w-full !min-h-[54px] text-base disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                `Continue — $${priceUsd} / week`
              )}
            </button>
            <p className="mt-2 text-center text-[12px] text-muted-foreground">
              Apple Pay and all major cards, securely via Stripe.
            </p>
          </>
        ) : (
          <div className="mt-4">
            <PhoneLinkCard
              compact
              title="First, one quick step"
              subtitle="Verify your phone number so your subscription follows you on every device — then continue to payment."
              onLinked={(phone) => {
                setAuth(phone);
                setSignedIn(true);
              }}
            />
          </div>
        )}
        {error ? (
          <p className="mt-2 text-center text-[13px]" style={{ color: "hsl(0 60% 42%)" }}>
            {error}
          </p>
        ) : null}
        {onClose ? (
          <button
            type="button"
            data-testid="paywall-close-btn"
            onClick={onClose}
            className="mt-4 w-full text-center text-[13px] text-muted-foreground underline underline-offset-4"
          >
            Not now — return home
          </button>
        ) : null}
      </div>
    </div>
  );
}
